import fs from 'node:fs'
import path from 'node:path'
import * as p from '@clack/prompts'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import {
  type LoadedSchema,
  listSchemaNames,
  loadSchema
} from '@core-engine/schema/schema-reader.js'
import { loadManifest } from '@core-engine/snapshots/store.js'
import {
  appendEnvVars,
  type EnvSection,
  readEnvVar,
  removeEnvVars
} from '@core-engine/utils/env.js'
import type { PackageManager } from '@core-engine/utils/package-manager.js'
import {
  deleteInstalledIntegrationManifest,
  loadInstalledIntegrationManifest,
  saveInstalledIntegrationManifest
} from '@integration-engine/manifests.js'
import type {
  BetterstartIntegrationDefinition,
  BetterstartIntegrationId,
  InstalledIntegrationManifest
} from '@integration-engine/types.js'
import {
  applyAdminNamespaceToContent,
  applyAdminNamespaceToPath
} from '@next-adapter/config/namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import {
  getDependencyPlan,
  installDependenciesAsync,
  removeDependenciesAsync
} from '@next-adapter/init/scaffolders/dependencies.js'
import {
  formatUnknownIntegrationMessage,
  getIntegrationDefinition,
  isIntegrationId,
  listIntegrationDefinitions
} from '@next-adapter/integration-registry.js'
import { readIntegrationTemplate } from '@next-adapter/integration-template-reader.js'
import { getPresetDefinition } from '@next-adapter/preset-registry.js'
import { readTemplate } from '@next-adapter/template-reader.js'
import { loadSchemaOwnershipIndex } from '@preset-engine/ownership.js'

interface ConfigureIntegrationResult {
  configured: boolean
  addedEnvKeys: string[]
  updatedEnvKeys: string[]
}

export interface CollectedIntegrationConfig {
  sections: EnvSection[]
  overwriteKeys: Set<string>
}

export interface InstallIntegrationsOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  pm: PackageManager
  integrationIds: BetterstartIntegrationId[]
  interactive: boolean
  includeBiome: boolean
}

export interface InstallIntegrationsResult {
  installed: BetterstartIntegrationId[]
  activated: BetterstartIntegrationId[]
  skipped: BetterstartIntegrationId[]
  warnings: string[]
  config: ResolvedBetterstartConfig
}

export interface RemoveIntegrationsOptions {
  cwd: string
  config: ResolvedBetterstartConfig
  pm: PackageManager
  integrationIds: BetterstartIntegrationId[]
}

export interface RemoveIntegrationsResult {
  removed: BetterstartIntegrationId[]
  warnings: string[]
  config: ResolvedBetterstartConfig
}

function unique<T>(values: T[]): T[] {
  return Array.from(new Set(values))
}

function normalizeInstalledIntegrations(
  config: ResolvedBetterstartConfig
): BetterstartIntegrationId[] {
  return unique((config.integrations.installed ?? []).filter(isIntegrationId)).sort()
}

function resolveIntegrationInstallOrder(
  integrationIds: BetterstartIntegrationId[]
): BetterstartIntegrationId[] {
  const ordered: BetterstartIntegrationId[] = []
  const visiting = new Set<BetterstartIntegrationId>()
  const visited = new Set<BetterstartIntegrationId>()

  function visit(integrationId: BetterstartIntegrationId): void {
    if (visited.has(integrationId)) return
    if (visiting.has(integrationId)) {
      throw new Error(`Circular integration dependency detected at "${integrationId}".`)
    }

    visiting.add(integrationId)
    for (const dependency of getIntegrationDefinition(integrationId).dependencies) {
      visit(dependency)
    }
    visiting.delete(integrationId)
    visited.add(integrationId)
    ordered.push(integrationId)
  }

  for (const integrationId of integrationIds) {
    visit(integrationId)
  }

  return ordered
}

function assertNoIntegrationConflicts(
  integrationIds: BetterstartIntegrationId[],
  installedIntegrationIds: BetterstartIntegrationId[]
): void {
  const combined = unique([...installedIntegrationIds, ...integrationIds])

  for (const integrationId of combined) {
    const definition = getIntegrationDefinition(integrationId)
    const conflict = combined.find(
      (candidate) =>
        candidate !== integrationId &&
        (definition.conflicts.includes(candidate) ||
          getIntegrationDefinition(candidate).conflicts.includes(integrationId))
    )
    if (conflict) {
      throw new Error(
        `Integration "${integrationId}" conflicts with "${conflict}". Remove one provider before continuing.`
      )
    }
  }
}

function resolveIntegrationProjectPath(
  config: ResolvedBetterstartConfig,
  outputPath: string
): string {
  const namespacedOutputPath = applyAdminNamespaceToPath(
    outputPath,
    config.frameworkConfig.next.namespace
  )
  return path.posix.join(config.paths.admin.replace(/^\.\//, ''), namespacedOutputPath)
}

function namespaceIntegrationContent(config: ResolvedBetterstartConfig, content: string): string {
  return applyAdminNamespaceToContent(content, config.frameworkConfig.next.namespace)
}

function ensureIntegrationRuntimeSupport(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartIntegrationDefinition
): void {
  if (definition.capabilities?.storageProvider?.provider !== 'railway-bucket') return

  const routePath = path.resolve(cwd, config.paths.api, 'storage', '[key]', 'route.ts')
  if (fs.existsSync(routePath)) return

  fs.mkdirSync(path.dirname(routePath), { recursive: true })
  fs.writeFileSync(
    routePath,
    namespaceIntegrationContent(config, readTemplate('api/storage-read-route.ts')),
    'utf-8'
  )
}

function flattenEnvKeys(definition: BetterstartIntegrationDefinition): string[] {
  return definition.envSections.flatMap((section) => section.vars.map((entry) => entry.key))
}

function normalizeManagedFileContent(content: string): string {
  return content.replace(/\r\n/g, '\n')
}

function getEmailTemplatesDir(cwd: string, config: ResolvedBetterstartConfig): string {
  const paths = resolveProjectPaths(config)
  return path.join(cwd, ...paths.adminEmailTemplatesDir.split('/'))
}

function ensureEmailTemplatesDir(cwd: string, config: ResolvedBetterstartConfig): void {
  fs.mkdirSync(getEmailTemplatesDir(cwd, config), { recursive: true })
}

function removeEmailTemplatesDirIfEmpty(cwd: string, config: ResolvedBetterstartConfig): void {
  const dir = getEmailTemplatesDir(cwd, config)
  if (!fs.existsSync(dir)) return

  if (fs.readdirSync(dir).length === 0) {
    fs.rmdirSync(dir)
  }
}

function getActiveEmailProvider(config: ResolvedBetterstartConfig) {
  return normalizeInstalledIntegrations(config)
    .map((integrationId) => getIntegrationDefinition(integrationId))
    .map((definition) => definition.capabilities?.emailProvider)
    .find((provider) => provider?.provider === config.email.provider)
}

function getActiveStorageProvider(config: ResolvedBetterstartConfig) {
  return normalizeInstalledIntegrations(config)
    .map((integrationId) => getIntegrationDefinition(integrationId))
    .map((definition) => definition.capabilities?.storageProvider)
    .find((provider) => provider?.provider === config.storage.provider)
}

export function renderCoreEmailProvider(config: ResolvedBetterstartConfig): string {
  const activeProvider = getActiveEmailProvider(config)
  const providerImport = activeProvider
    ? `import { ${activeProvider.exportName} } from '${activeProvider.importPath}'\n`
    : ''
  const providerBranch = activeProvider
    ? `  if (${activeProvider.exportName}.isConfigured()) {
    return ${activeProvider.exportName}
  }

`
    : ''

  return namespaceIntegrationContent(
    config,
    `import { noneEmailProvider } from '@admin/actions/email/none'
${providerImport}import type { AdminEmailProvider } from '@admin/actions/email/types'

export function getEmailProvider(): AdminEmailProvider {
${providerBranch}  return noneEmailProvider
}
`
  )
}

export function renderCoreEmailProviderIndex(config: ResolvedBetterstartConfig): string {
  return namespaceIntegrationContent(config, readTemplate('lib/actions/email/index.ts'))
}

export function renderCoreStorageProvider(config: ResolvedBetterstartConfig): string {
  const activeProvider = getActiveStorageProvider(config)
  const providerImport = activeProvider
    ? `import { ${activeProvider.exportName} } from '${activeProvider.importPath}'\n`
    : ''
  const providerBranch = activeProvider
    ? `  if (${activeProvider.exportName}.isConfigured()) {
    return ${activeProvider.exportName}
  }

`
    : ''

  return namespaceIntegrationContent(
    config,
    `import { localStorageProvider } from '@admin/actions/storage/local'
${providerImport}import type { AdminStorageProvider } from '@admin/actions/storage/types'

export function getStorageProvider(): AdminStorageProvider {
${providerBranch}  return localStorageProvider
}
`
  )
}

export function renderCoreStorageProviderIndex(config: ResolvedBetterstartConfig): string {
  return namespaceIntegrationContent(config, readTemplate('lib/actions/storage/index.ts'))
}

function writeCoreProviderIndexes(cwd: string, config: ResolvedBetterstartConfig): void {
  const paths = resolveProjectPaths(config)
  const emailPath = path.join(cwd, ...paths.adminActionsDir.split('/'), 'email', 'index.ts')
  const emailProviderPath = path.join(
    cwd,
    ...paths.adminActionsDir.split('/'),
    'email',
    'provider.ts'
  )
  const storagePath = path.join(cwd, ...paths.adminActionsDir.split('/'), 'storage', 'index.ts')
  const storageProviderPath = path.join(
    cwd,
    ...paths.adminActionsDir.split('/'),
    'storage',
    'provider.ts'
  )

  fs.mkdirSync(path.dirname(emailPath), { recursive: true })
  fs.writeFileSync(emailPath, renderCoreEmailProviderIndex(config), 'utf-8')
  fs.writeFileSync(emailProviderPath, renderCoreEmailProvider(config), 'utf-8')

  fs.mkdirSync(path.dirname(storagePath), { recursive: true })
  fs.writeFileSync(storagePath, renderCoreStorageProviderIndex(config), 'utf-8')
  fs.writeFileSync(storageProviderPath, renderCoreStorageProvider(config), 'utf-8')
}

function tryReadCoreManagedTemplate(relativeOutputPath: string): string | null {
  try {
    return readTemplate(relativeOutputPath)
  } catch {
    return null
  }
}

function hasRequiredIntegrationEnv(
  definition: BetterstartIntegrationDefinition,
  cwd: string
): boolean {
  const requiredKeys = definition.envSections.flatMap((section) =>
    section.vars.filter((entry) => entry.value === '').map((entry) => entry.key)
  )
  return requiredKeys.every((key) => Boolean(readEnvVar(cwd, key)))
}

function readNonEmptyEnvVar(cwd: string, key: string): string | undefined {
  const value = readEnvVar(cwd, key)?.trim()
  return value ? value : undefined
}

interface EnvPromptOptions {
  cwd: string
  key: string
  message: string
  cancelMessage: string
  overwriteEnvKeys: Set<string>
  validate: (value: string | undefined) => string | Error | undefined
}

interface EnvTextPromptOptions extends EnvPromptOptions {
  defaultValue?: string
}

async function resolveTextEnvValue(options: EnvTextPromptOptions): Promise<string> {
  const existingValue = readNonEmptyEnvVar(options.cwd, options.key)
  if (existingValue) {
    return existingValue
  }

  const result = await p.text({
    message: options.message,
    defaultValue: options.defaultValue,
    validate: options.validate
  })

  if (p.isCancel(result)) {
    throw new Error(options.cancelMessage)
  }

  options.overwriteEnvKeys.add(options.key)
  return result.trim()
}

async function resolvePasswordEnvValue(options: EnvPromptOptions): Promise<string> {
  const existingValue = readNonEmptyEnvVar(options.cwd, options.key)
  if (existingValue) {
    return existingValue
  }

  const result = await p.password({
    message: options.message,
    validate: options.validate
  })

  if (p.isCancel(result)) {
    throw new Error(options.cancelMessage)
  }

  options.overwriteEnvKeys.add(options.key)
  return result.trim()
}

async function collectResendConfig(cwd: string): Promise<CollectedIntegrationConfig> {
  const overwriteEnvKeys = new Set<string>()
  const apiKey = await resolvePasswordEnvValue({
    cwd,
    key: 'BETTERSTART_RESEND_API_KEY',
    message: 'Resend API key',
    cancelMessage: 'Resend configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) {
        return 'Enter a Resend API key.'
      }
    }
  })
  const fromAddress = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_EMAIL_FROM',
    message: 'From email address',
    defaultValue: 'noreply@yourdomain.com',
    cancelMessage: 'Resend configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim() || !value.includes('@')) {
        return 'Enter a valid sender email address.'
      }
    }
  })

  return {
    sections: [
      {
        header: 'Email (Resend)',
        vars: [
          { key: 'BETTERSTART_RESEND_API_KEY', value: apiKey.trim() },
          { key: 'BETTERSTART_EMAIL_FROM', value: fromAddress.trim() }
        ]
      }
    ],
    overwriteKeys: overwriteEnvKeys
  }
}

async function promptResendConfig(cwd: string): Promise<ConfigureIntegrationResult> {
  const collected = await collectResendConfig(cwd)
  const envResult = appendEnvVars(cwd, collected.sections, collected.overwriteKeys)

  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

async function collectR2Config(cwd: string): Promise<CollectedIntegrationConfig> {
  const overwriteEnvKeys = new Set<string>()
  const accountId = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_R2_ACCOUNT_ID',
    message: 'Cloudflare account ID',
    cancelMessage: 'R2 configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter your Cloudflare account ID.'
    }
  })
  const accessKeyId = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_R2_ACCESS_KEY_ID',
    message: 'Cloudflare R2 access key ID',
    cancelMessage: 'R2 configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter your R2 access key ID.'
    }
  })
  const secretAccessKey = await resolvePasswordEnvValue({
    cwd,
    key: 'BETTERSTART_R2_SECRET_ACCESS_KEY',
    message: 'Cloudflare R2 secret access key',
    cancelMessage: 'R2 configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter your R2 secret access key.'
    }
  })
  const bucketName = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_R2_BUCKET_NAME',
    message: 'Cloudflare R2 bucket name',
    defaultValue: 'betterstart-uploads',
    cancelMessage: 'R2 configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter your R2 bucket name.'
    }
  })
  const publicUrl = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_R2_PUBLIC_URL',
    message: 'Cloudflare R2 public URL',
    cancelMessage: 'R2 configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim() || !value.startsWith('http')) {
        return 'Enter a valid public URL.'
      }
    }
  })

  return {
    sections: [
      {
        header: 'Storage (Cloudflare R2)',
        vars: [
          { key: 'BETTERSTART_R2_ACCOUNT_ID', value: accountId.trim() },
          { key: 'BETTERSTART_R2_ACCESS_KEY_ID', value: accessKeyId.trim() },
          { key: 'BETTERSTART_R2_SECRET_ACCESS_KEY', value: secretAccessKey.trim() },
          { key: 'BETTERSTART_R2_BUCKET_NAME', value: bucketName.trim() },
          { key: 'BETTERSTART_R2_PUBLIC_URL', value: publicUrl.trim() }
        ]
      }
    ],
    overwriteKeys: overwriteEnvKeys
  }
}

async function promptR2Config(cwd: string): Promise<ConfigureIntegrationResult> {
  const collected = await collectR2Config(cwd)
  const envResult = appendEnvVars(cwd, collected.sections, collected.overwriteKeys)

  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

async function collectRailwayBucketConfig(cwd: string): Promise<CollectedIntegrationConfig> {
  const overwriteEnvKeys = new Set<string>()
  const endpoint = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_RAILWAY_BUCKET_ENDPOINT',
    message: 'Railway bucket S3 endpoint',
    cancelMessage: 'Railway Bucket configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      try {
        if (!value?.trim()) throw new Error()
        new URL(value)
      } catch {
        return 'Enter a valid Railway bucket endpoint URL.'
      }
    }
  })
  const accessKeyId = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_RAILWAY_BUCKET_ACCESS_KEY_ID',
    message: 'Railway bucket access key ID',
    cancelMessage: 'Railway Bucket configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter the Railway bucket access key ID.'
    }
  })
  const secretAccessKey = await resolvePasswordEnvValue({
    cwd,
    key: 'BETTERSTART_RAILWAY_BUCKET_SECRET_ACCESS_KEY',
    message: 'Railway bucket secret access key',
    cancelMessage: 'Railway Bucket configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter the Railway bucket secret access key.'
    }
  })
  const bucketName = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_RAILWAY_BUCKET_NAME',
    message: 'Railway bucket name',
    defaultValue: 'media',
    cancelMessage: 'Railway Bucket configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter the Railway bucket name.'
    }
  })
  const region = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_RAILWAY_BUCKET_REGION',
    message: 'Railway bucket region',
    defaultValue: 'iad',
    cancelMessage: 'Railway Bucket configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter the Railway bucket region.'
    }
  })
  const urlStyle = await resolveTextEnvValue({
    cwd,
    key: 'BETTERSTART_RAILWAY_BUCKET_URL_STYLE',
    message: 'Railway bucket URL style',
    defaultValue: 'path',
    cancelMessage: 'Railway Bucket configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) return 'Enter the Railway bucket URL style.'
    }
  })

  return {
    sections: [
      {
        header: 'Storage (Railway Bucket)',
        vars: [
          { key: 'BETTERSTART_RAILWAY_BUCKET_ENDPOINT', value: endpoint.trim() },
          {
            key: 'BETTERSTART_RAILWAY_BUCKET_ACCESS_KEY_ID',
            value: accessKeyId.trim()
          },
          {
            key: 'BETTERSTART_RAILWAY_BUCKET_SECRET_ACCESS_KEY',
            value: secretAccessKey.trim()
          },
          { key: 'BETTERSTART_RAILWAY_BUCKET_NAME', value: bucketName.trim() },
          { key: 'BETTERSTART_RAILWAY_BUCKET_REGION', value: region.trim() },
          { key: 'BETTERSTART_RAILWAY_BUCKET_URL_STYLE', value: urlStyle.trim() }
        ]
      }
    ],
    overwriteKeys: overwriteEnvKeys
  }
}

async function promptRailwayBucketConfig(cwd: string): Promise<ConfigureIntegrationResult> {
  const collected = await collectRailwayBucketConfig(cwd)
  const envResult = appendEnvVars(cwd, collected.sections, collected.overwriteKeys)

  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

async function collectVercelBlobConfig(cwd: string): Promise<CollectedIntegrationConfig> {
  const overwriteEnvKeys = new Set<string>()
  const token = await resolvePasswordEnvValue({
    cwd,
    key: 'BLOB_READ_WRITE_TOKEN',
    message: 'Vercel Blob read-write token (from the store on vercel.com/dashboard/stores)',
    cancelMessage: 'Vercel Blob configuration cancelled.',
    overwriteEnvKeys,
    validate(value) {
      if (!value?.trim()) {
        return 'Enter a Vercel Blob read-write token.'
      }
    }
  })

  return {
    sections: [
      {
        header: 'Storage (Vercel Blob)',
        vars: [{ key: 'BLOB_READ_WRITE_TOKEN', value: token.trim() }]
      }
    ],
    overwriteKeys: overwriteEnvKeys
  }
}

async function promptVercelBlobConfig(cwd: string): Promise<ConfigureIntegrationResult> {
  const collected = await collectVercelBlobConfig(cwd)
  const envResult = appendEnvVars(cwd, collected.sections, collected.overwriteKeys)

  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

/**
 * Prompt for the secrets of the given integrations without writing anything.
 * Used by interactive `init` to gather all integration config up front, before
 * scaffolding runs, so the install step never stops to ask for input. Returns
 * the collected env sections to be written once `.env.local` exists.
 */
export async function collectIntegrationConfig(
  cwd: string,
  integrationIds: BetterstartIntegrationId[]
): Promise<CollectedIntegrationConfig> {
  const sections: EnvSection[] = []
  const overwriteKeys = new Set<string>()

  for (const integrationId of resolveIntegrationInstallOrder(integrationIds)) {
    const definition = getIntegrationDefinition(integrationId)
    const collected =
      definition.configure === 'resend'
        ? await collectResendConfig(cwd)
        : definition.configure === 'r2'
          ? await collectR2Config(cwd)
          : definition.configure === 'vercel-blob'
            ? await collectVercelBlobConfig(cwd)
            : definition.configure === 'railway-bucket'
              ? await collectRailwayBucketConfig(cwd)
              : undefined

    if (!collected) {
      continue
    }

    sections.push(...collected.sections)
    for (const key of collected.overwriteKeys) {
      overwriteKeys.add(key)
    }
  }

  return { sections, overwriteKeys }
}

async function configureIntegration(
  definition: BetterstartIntegrationDefinition,
  cwd: string,
  interactive: boolean
): Promise<ConfigureIntegrationResult> {
  if (definition.configure === 'resend') {
    if (interactive) {
      return promptResendConfig(cwd)
    }

    const envResult = appendEnvVars(cwd, definition.envSections)
    return {
      configured: hasRequiredIntegrationEnv(definition, cwd),
      addedEnvKeys: envResult.added,
      updatedEnvKeys: envResult.updated
    }
  }

  if (definition.configure === 'r2') {
    if (interactive) {
      return promptR2Config(cwd)
    }

    const envResult = appendEnvVars(cwd, definition.envSections)
    return {
      configured: hasRequiredIntegrationEnv(definition, cwd),
      addedEnvKeys: envResult.added,
      updatedEnvKeys: envResult.updated
    }
  }

  if (definition.configure === 'vercel-blob') {
    if (interactive) {
      return promptVercelBlobConfig(cwd)
    }

    const envResult = appendEnvVars(cwd, definition.envSections)
    return {
      configured: hasRequiredIntegrationEnv(definition, cwd),
      addedEnvKeys: envResult.added,
      updatedEnvKeys: envResult.updated
    }
  }

  if (definition.configure === 'railway-bucket') {
    if (interactive) {
      return promptRailwayBucketConfig(cwd)
    }

    const envResult = appendEnvVars(cwd, definition.envSections)
    return {
      configured: hasRequiredIntegrationEnv(definition, cwd),
      addedEnvKeys: envResult.added,
      updatedEnvKeys: envResult.updated
    }
  }

  const envResult = appendEnvVars(cwd, definition.envSections)
  return {
    configured: true,
    addedEnvKeys: envResult.added,
    updatedEnvKeys: envResult.updated
  }
}

function ensureIntegrationDirectFileTargetsAvailable(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartIntegrationDefinition
): void {
  for (const file of definition.files) {
    const projectRelativePath = resolveIntegrationProjectPath(config, file.outputPath)
    const fullPath = path.join(cwd, ...projectRelativePath.split('/'))

    if (!fs.existsSync(fullPath)) {
      continue
    }

    const stats = fs.statSync(fullPath)
    if (!stats.isFile()) {
      throw new Error(
        `Installing ${definition.id} would overwrite "${projectRelativePath}", but it is not a file. Remove or rename it before continuing.`
      )
    }

    const existingContent = normalizeManagedFileContent(fs.readFileSync(fullPath, 'utf-8'))
    const integrationTemplate = normalizeManagedFileContent(
      namespaceIntegrationContent(config, readIntegrationTemplate(definition.id, file.templatePath))
    )

    if (existingContent === integrationTemplate) {
      continue
    }

    const coreTemplate = tryReadCoreManagedTemplate(file.outputPath)
    if (
      coreTemplate &&
      existingContent ===
        normalizeManagedFileContent(namespaceIntegrationContent(config, coreTemplate))
    ) {
      continue
    }

    throw new Error(
      `Installing ${definition.id} would overwrite existing file "${projectRelativePath}". Rename, remove, or reset it to the managed template before installing ${definition.id}.`
    )
  }
}

function writeIntegrationDirectFiles(
  cwd: string,
  config: ResolvedBetterstartConfig,
  definition: BetterstartIntegrationDefinition
): string[] {
  const writtenFiles: string[] = []

  for (const file of definition.files) {
    const projectRelativePath = resolveIntegrationProjectPath(config, file.outputPath)
    const fullPath = path.join(cwd, ...projectRelativePath.split('/'))
    fs.mkdirSync(path.dirname(fullPath), { recursive: true })
    fs.writeFileSync(
      fullPath,
      namespaceIntegrationContent(
        config,
        readIntegrationTemplate(definition.id, file.templatePath)
      ),
      'utf-8'
    )
    writtenFiles.push(projectRelativePath)
  }

  return writtenFiles
}

function toProjectAbsolutePath(cwd: string, filePath: string): string {
  return path.isAbsolute(filePath) ? filePath : path.join(cwd, ...filePath.split('/'))
}

function isInsideDir(child: string, parent: string): boolean {
  const relative = path.relative(parent, child)
  return relative === '' || (!relative.startsWith('..') && !path.isAbsolute(relative))
}

function cleanupEmptyDirs(
  cwd: string,
  deletedPaths: string[],
  config: ResolvedBetterstartConfig
): void {
  const paths = resolveProjectPaths(config)
  const projectRoot = path.resolve(cwd)
  const stopRoots = new Set([
    projectRoot,
    toProjectAbsolutePath(cwd, paths.adminDir),
    toProjectAbsolutePath(cwd, paths.pagesDir),
    toProjectAbsolutePath(cwd, paths.schemasDir),
    path.join(projectRoot, '.betterstart'),
    path.join(projectRoot, '.betterstart', 'integrations'),
    path.join(projectRoot, '.betterstart', 'snapshots')
  ])

  for (const deletedPath of deletedPaths) {
    let currentDir = path.dirname(toProjectAbsolutePath(cwd, deletedPath))

    while (isInsideDir(currentDir, projectRoot) && !stopRoots.has(currentDir)) {
      if (!fs.existsSync(currentDir)) {
        currentDir = path.dirname(currentDir)
        continue
      }

      if (fs.readdirSync(currentDir).length > 0) {
        break
      }

      fs.rmdirSync(currentDir)
      currentDir = path.dirname(currentDir)
    }
  }
}

function restoreOrDeleteIntegrationFile(
  cwd: string,
  config: ResolvedBetterstartConfig,
  relativeOutputPath: string
): string | null {
  const projectRelativePath = resolveIntegrationProjectPath(config, relativeOutputPath)
  const fullPath = toProjectAbsolutePath(cwd, projectRelativePath)

  try {
    const coreTemplate = readTemplate(relativeOutputPath)
    fs.mkdirSync(path.dirname(fullPath), { recursive: true })
    fs.writeFileSync(fullPath, namespaceIntegrationContent(config, coreTemplate), 'utf-8')
    return null
  } catch {
    const existed = fs.existsSync(fullPath)
    fs.rmSync(fullPath, { force: true })
    return existed ? projectRelativePath : null
  }
}

function schemaManifestContainsMailchimpImports(
  cwd: string,
  config: ResolvedBetterstartConfig,
  schemaName: string
): boolean {
  const manifest = loadManifest(cwd, schemaName)
  if (!manifest) {
    return false
  }

  const namespace = config.frameworkConfig.next.namespace
  const markers = [
    applyAdminNamespaceToContent('@admin/actions/mailchimp/add-to-mailchimp-audience', namespace),
    applyAdminNamespaceToContent('@admin/actions/mailchimp/mailchimp', namespace),
    'addToMailchimpAudience('
  ]
  const liveManifestPaths = unique([
    ...manifest.files.map((file) => file.path),
    ...manifest.skipped
  ])

  return liveManifestPaths.some((filePath) => {
    const fullPath = path.join(cwd, ...filePath.split('/'))
    if (!fs.existsSync(fullPath) || !fs.statSync(fullPath).isFile()) {
      return false
    }

    const content = fs.readFileSync(fullPath, 'utf-8')
    return markers.some((marker) => content.includes(marker))
  })
}

function findBlockingIntegrationDependencies(
  cwd: string,
  config: ResolvedBetterstartConfig,
  integrationId: BetterstartIntegrationId
): string[] {
  const blockers: string[] = []
  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))
  const ownership = loadSchemaOwnershipIndex(cwd)

  for (const schemaName of listSchemaNames(schemasDir)) {
    if (ownership.schemas[schemaName] !== 'user') {
      continue
    }

    const loaded: LoadedSchema = loadSchema(schemasDir, schemaName)
    if (integrationId === 'mailchimp' && loaded.type === 'form') {
      if (loaded.schema.mailchimp) {
        blockers.push(`${schemaName} still sets "mailchimp" in its form schema`)
        continue
      }

      if (schemaManifestContainsMailchimpImports(cwd, config, schemaName)) {
        blockers.push(
          `${schemaName} still has generated Mailchimp wiring. Run \`betterstart generate ${schemaName}\` before removing the mailchimp integration`
        )
      }
    }
  }

  return blockers
}

function getUnusedIntegrationDependencies(
  config: ResolvedBetterstartConfig,
  remainingIntegrations: BetterstartIntegrationId[],
  removedIntegrationId: BetterstartIntegrationId
): { dependencies: string[]; devDependencies: string[] } {
  const remainingIntegrationDefinitions = remainingIntegrations.map((integrationId) =>
    getIntegrationDefinition(integrationId)
  )
  const remainingPresetDefinitions = config.presets.installed.map((presetId) =>
    getPresetDefinition(presetId)
  )
  const remainingDependencies = new Set([
    ...remainingIntegrationDefinitions.flatMap((definition) => definition.packageDependencies),
    ...remainingPresetDefinitions.flatMap((definition) => definition.packageDependencies)
  ])
  const remainingDevDependencies = new Set([
    ...remainingIntegrationDefinitions.flatMap((definition) => definition.devDependencies),
    ...remainingPresetDefinitions.flatMap((definition) => definition.devDependencies)
  ])

  const corePlan = getDependencyPlan([], [], false)
  const removedDefinition = getIntegrationDefinition(removedIntegrationId)

  return {
    dependencies: removedDefinition.packageDependencies.filter(
      (dependency) =>
        !remainingDependencies.has(dependency) && !corePlan.dependencies.includes(dependency)
    ),
    devDependencies: removedDefinition.devDependencies.filter(
      (dependency) =>
        !remainingDevDependencies.has(dependency) && !corePlan.devDependencies.includes(dependency)
    )
  }
}

export function parseIntegrationList(value: string | undefined): BetterstartIntegrationId[] {
  if (!value) {
    return []
  }

  const integrationIds = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

  const invalid = integrationIds.filter((integrationId) => !isIntegrationId(integrationId))
  if (invalid.length > 0) {
    throw new Error(formatUnknownIntegrationMessage(invalid))
  }

  return unique(integrationIds as BetterstartIntegrationId[])
}

export function getInstalledIntegrations(
  config: ResolvedBetterstartConfig
): BetterstartIntegrationId[] {
  return normalizeInstalledIntegrations(config)
}

export function hasInstalledIntegrationFeature(
  config: ResolvedBetterstartConfig,
  feature: string
): boolean {
  return normalizeInstalledIntegrations(config).some((integrationId) =>
    getIntegrationDefinition(integrationId).capabilities?.formFeatures?.includes(feature)
  )
}

export function syncInstalledIntegrationManifests(
  cwd: string,
  config: ResolvedBetterstartConfig
): void {
  for (const integrationId of normalizeInstalledIntegrations(config)) {
    const manifest = loadInstalledIntegrationManifest(cwd, integrationId)
    if (!manifest) {
      continue
    }

    const definition = getIntegrationDefinition(integrationId)
    const nextManifest: InstalledIntegrationManifest = {
      ...manifest,
      version: definition.version,
      kind: definition.kind,
      ownedFiles: definition.files.map((file) =>
        resolveIntegrationProjectPath(config, file.outputPath)
      ),
      packageDependencies: definition.packageDependencies,
      devDependencies: definition.devDependencies,
      dependencies: definition.dependencies,
      conflicts: definition.conflicts
    }

    if (JSON.stringify(nextManifest) !== JSON.stringify(manifest)) {
      saveInstalledIntegrationManifest(cwd, nextManifest)
    }
  }
}

function isProviderIntegration(definition: BetterstartIntegrationDefinition): boolean {
  return Boolean(definition.capabilities?.emailProvider || definition.capabilities?.storageProvider)
}

function activateConfiguredProviderIntegration(
  definition: BetterstartIntegrationDefinition,
  config: ResolvedBetterstartConfig
): boolean {
  let changed = false
  const emailProvider = definition.capabilities?.emailProvider
  const storageProvider = definition.capabilities?.storageProvider

  if (emailProvider && config.email.provider !== emailProvider.provider) {
    config.email.provider = emailProvider.provider
    changed = true
  }

  if (storageProvider && config.storage.provider !== storageProvider.provider) {
    config.storage.provider = storageProvider.provider
    changed = true
  }

  return changed
}

function buildInstalledIntegrationManifest({
  config,
  definition,
  writtenFiles,
  configured,
  previousManifest
}: {
  config: ResolvedBetterstartConfig
  definition: BetterstartIntegrationDefinition
  writtenFiles?: string[]
  configured: boolean
  previousManifest?: InstalledIntegrationManifest | null
}): InstalledIntegrationManifest {
  return {
    id: definition.id,
    version: definition.version,
    kind: definition.kind,
    ownedFiles:
      writtenFiles ??
      previousManifest?.ownedFiles ??
      definition.files.map((file) => resolveIntegrationProjectPath(config, file.outputPath)),
    envKeys: flattenEnvKeys(definition),
    packageDependencies: definition.packageDependencies,
    devDependencies: definition.devDependencies,
    configured,
    dependencies: definition.dependencies,
    conflicts: definition.conflicts,
    installedAt: previousManifest?.installedAt ?? new Date().toISOString()
  }
}

export async function installIntegrations({
  cwd,
  config,
  pm,
  integrationIds,
  interactive,
  includeBiome
}: InstallIntegrationsOptions): Promise<InstallIntegrationsResult> {
  const orderedIntegrationIds = resolveIntegrationInstallOrder(integrationIds)
  const installedIntegrationIds = normalizeInstalledIntegrations(config)
  assertNoIntegrationConflicts(orderedIntegrationIds, installedIntegrationIds)
  const skipped: BetterstartIntegrationId[] = []
  const installed: BetterstartIntegrationId[] = []
  const activated: BetterstartIntegrationId[] = []
  const warnings: string[] = []

  for (const integrationId of orderedIntegrationIds) {
    const definition = getIntegrationDefinition(integrationId)

    if (installedIntegrationIds.includes(integrationId)) {
      if (!isProviderIntegration(definition)) {
        skipped.push(integrationId)
        continue
      }

      const previousManifest = loadInstalledIntegrationManifest(cwd, integrationId)
      const configureResult = await configureIntegration(definition, cwd, interactive)
      ensureIntegrationRuntimeSupport(cwd, config, definition)

      if (!configureResult.configured) {
        saveInstalledIntegrationManifest(
          cwd,
          buildInstalledIntegrationManifest({
            config,
            definition,
            configured: false,
            previousManifest
          })
        )
        skipped.push(integrationId)
        warnings.push(
          `${integrationId} is installed but was not activated because its required environment variables are still missing.`
        )
        continue
      }

      const providerChanged = activateConfiguredProviderIntegration(definition, config)

      if (definition.capabilities?.emailProvider) {
        ensureEmailTemplatesDir(cwd, config)
      }
      writeCoreProviderIndexes(cwd, config)
      saveInstalledIntegrationManifest(
        cwd,
        buildInstalledIntegrationManifest({
          config,
          definition,
          configured: true,
          previousManifest
        })
      )
      writeConfigFile(cwd, config)

      if (providerChanged || previousManifest?.configured !== true) {
        activated.push(integrationId)
      } else {
        skipped.push(integrationId)
      }
      continue
    }

    ensureIntegrationDirectFileTargetsAvailable(cwd, config, definition)

    const configureResult = await configureIntegration(definition, cwd, interactive)
    ensureIntegrationRuntimeSupport(cwd, config, definition)
    const dependencyPlan = getDependencyPlan(
      config.presets.installed,
      [...installedIntegrationIds, integrationId],
      includeBiome
    )
    const currentPlan = getDependencyPlan(
      config.presets.installed,
      installedIntegrationIds,
      includeBiome
    )

    const dependencyDiff = {
      dependencies: dependencyPlan.dependencies.filter(
        (dependency) => !currentPlan.dependencies.includes(dependency)
      ),
      devDependencies: dependencyPlan.devDependencies.filter(
        (dependency) => !currentPlan.devDependencies.includes(dependency)
      )
    }

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await installDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        throw new Error(result.error ?? `Failed to install dependencies for ${integrationId}.`)
      }
    }

    const writtenFiles = writeIntegrationDirectFiles(cwd, config, definition)

    installedIntegrationIds.push(integrationId)
    config.integrations.installed = unique(installedIntegrationIds).sort()

    if (configureResult.configured) {
      activateConfiguredProviderIntegration(definition, config)
    }

    if (definition.capabilities?.emailProvider) {
      ensureEmailTemplatesDir(cwd, config)
    }
    writeCoreProviderIndexes(cwd, config)

    const manifest = buildInstalledIntegrationManifest({
      config,
      definition,
      writtenFiles,
      configured: configureResult.configured
    })

    saveInstalledIntegrationManifest(cwd, manifest)
    writeConfigFile(cwd, config)
    installed.push(integrationId)

    if (!configureResult.configured && isProviderIntegration(definition)) {
      warnings.push(
        `${integrationId} was installed but not activated because its required environment variables are still missing.`
      )
    }
  }

  return {
    installed,
    activated,
    skipped,
    warnings,
    config
  }
}

export async function removeIntegrations({
  cwd,
  config,
  pm,
  integrationIds
}: RemoveIntegrationsOptions): Promise<RemoveIntegrationsResult> {
  const installedIntegrations = normalizeInstalledIntegrations(config)
  const removed: BetterstartIntegrationId[] = []
  const warnings: string[] = []

  for (const integrationId of integrationIds) {
    if (!installedIntegrations.includes(integrationId)) {
      warnings.push(`${integrationId} is not installed.`)
      continue
    }

    const manifest = loadInstalledIntegrationManifest(cwd, integrationId)
    if (!manifest) {
      throw new Error(`Missing manifest for installed integration "${integrationId}".`)
    }

    const dependents = installedIntegrations.filter((installedIntegrationId) => {
      if (installedIntegrationId === integrationId) {
        return false
      }

      return getIntegrationDefinition(installedIntegrationId).dependencies.includes(integrationId)
    })

    if (dependents.length > 0) {
      throw new Error(
        `Cannot remove ${integrationId}. The following integrations depend on it: ${dependents.join(', ')}`
      )
    }

    const blockers = findBlockingIntegrationDependencies(cwd, config, integrationId)
    if (blockers.length > 0) {
      throw new Error(`Cannot remove ${integrationId}:\n- ${blockers.join('\n- ')}`)
    }

    const deletedPaths: string[] = []

    for (const filePath of manifest.ownedFiles) {
      const relativeOutputPath = filePath.replace(`${config.paths.admin.replace(/^\.\//, '')}/`, '')
      const deletedPath = restoreOrDeleteIntegrationFile(cwd, config, relativeOutputPath)
      if (deletedPath) {
        deletedPaths.push(deletedPath)
      }
    }

    deleteInstalledIntegrationManifest(cwd, integrationId)
    const nextInstalledIntegrations = installedIntegrations.filter(
      (installedIntegrationId) => installedIntegrationId !== integrationId
    )
    installedIntegrations.splice(0, installedIntegrations.length, ...nextInstalledIntegrations)
    config.integrations.installed = [...nextInstalledIntegrations].sort()

    const definition = getIntegrationDefinition(integrationId)
    if (definition.capabilities?.emailProvider?.provider === config.email.provider) {
      config.email.provider = 'none'
    }
    if (definition.capabilities?.storageProvider?.provider === config.storage.provider) {
      config.storage.provider = 'local'
    }
    writeCoreProviderIndexes(cwd, config)

    const remainingIntegrations = normalizeInstalledIntegrations(config)
    const hasRemainingEmailProvider = remainingIntegrations.some((remainingIntegrationId) =>
      Boolean(getIntegrationDefinition(remainingIntegrationId).capabilities?.emailProvider)
    )
    if (definition.capabilities?.emailProvider && !hasRemainingEmailProvider) {
      removeEmailTemplatesDirIfEmpty(cwd, config)
    }

    const dependencyDiff = getUnusedIntegrationDependencies(
      config,
      remainingIntegrations,
      integrationId
    )

    if (dependencyDiff.dependencies.length > 0 || dependencyDiff.devDependencies.length > 0) {
      const result = await removeDependenciesAsync({
        cwd,
        pm,
        dependencies: dependencyDiff.dependencies,
        devDependencies: dependencyDiff.devDependencies
      })

      if (!result.success) {
        warnings.push(result.error ?? `Failed to remove dependencies for ${integrationId}.`)
      }
    }

    const usedEnvKeys = new Set(
      remainingIntegrations.flatMap((remainingIntegrationId) =>
        flattenEnvKeys(getIntegrationDefinition(remainingIntegrationId))
      )
    )
    const removableEnvKeys = manifest.envKeys.filter((envKey) => !usedEnvKeys.has(envKey))
    if (removableEnvKeys.length > 0) {
      removeEnvVars(cwd, removableEnvKeys)
    }

    cleanupEmptyDirs(cwd, deletedPaths, config)

    writeConfigFile(cwd, config)
    removed.push(integrationId)
  }

  return {
    removed,
    warnings,
    config
  }
}

export function listAvailableIntegrations() {
  return listIntegrationDefinitions()
}
